import { useState, useEffect, useCallback } from 'react'
import logger from '../utils/logger'

import { fetchVote, upvoteCall, downvoteCall } from '../services/bff'

/**
 * Custom hook to handle voting state and actions
 * Extracts voting logic from VotingHandler component for improved reusability
 */
export function useVoting (userId, rowsList) {
  const [voteStates, setVoteStates] = useState({})
  const [currentVote, setCurrentVote] = useState(null)
  const [selectedRow, setSelectedRow] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    const getVotes = async () => {
      const votes = {}

      await Promise.all(
        rowsList.map(async (row) => {
          const callId = row[3]
          if (!callId) return

          try {
            const data = await fetchVote(callId, userId)
            votes[callId] = data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null
          } catch (error) {
            votes[callId] = null
          }
        })
      )

      setVoteStates(votes)
    }

    if (rowsList?.length > 0 && userId) {
      getVotes()
    }
  }, [rowsList, userId])

  const handleRowClick = useCallback(async (row) => {
    const callId = row[3]
    if (!callId) {
      logger.error('No CALL ID found in this row.')
      return
    }

    setSelectedRow({ ...row, id: callId })

    try {
      const data = await fetchVote(callId, userId)
      setCurrentVote(data.vote === 1 ? 'up' : data.vote === -1 ? 'down' : null)
    } catch (error) {
      logger.error('Error fetching vote for call:', error)
      setCurrentVote(null)
    }

    setDrawerOpen(true)
  }, [userId])

  const handleUpvote = useCallback(async () => {
    if (!selectedRow || !selectedRow.id) {
      logger.error('No call ID available for upvote.')
      return
    }

    const success = await upvoteCall(selectedRow.id, userId)

    if (success) {
      setCurrentVote('up')
      setVoteStates((prev) => ({ ...prev, [selectedRow.id]: 'up' }))
      return true
    } else {
      logger.error('Upvote failed.')
      return false
    }
  }, [selectedRow, userId])

  const handleDownvote = useCallback(async () => {
    if (!selectedRow || !selectedRow.id) {
      logger.error('No call ID available for downvote.')
      return
    }

    const success = await downvoteCall(selectedRow.id, userId)

    if (success) {
      setCurrentVote('down')
      setVoteStates((prev) => ({ ...prev, [selectedRow.id]: 'down' }))
      return true
    } else {
      logger.error('Downvote failed.')
      return false
    }
  }, [selectedRow, userId])

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false)
  }, [])

  const openDrawerWithRow = useCallback((row) => {
    const callId = row.id || row[3]
    setSelectedRow({ ...row, id: callId })
    setDrawerOpen(true)
  }, [])

  return {
    voteStates,
    currentVote,
    selectedRow,
    drawerOpen,
    onRowClick: handleRowClick,
    onUpvote: handleUpvote,
    onDownvote: handleDownvote,
    onCloseDrawer: handleCloseDrawer,
    openDrawerWithRow
  }
}

export default useVoting
